import React from 'react';

import { css, cx } from '@emotion/css';
import { Skeleton } from '@mui/material';

import { FontSizes } from '@/assets/fonts.ts';
import { usePatientFromURL } from '@/hooks/usePatientIdFromURL.ts';
import { useStyles } from '@/hooks/useStyles.ts';
import { Theme } from '@/theme/CreateTheme.ts';
import { PatientActions } from '@/uiKit/molecules/PatientNavigation/PatientActions.tsx';
import { PatientHeaderInfo } from '@/uiKit/molecules/PatientNavigation/PatientHeaderInfo.tsx';
import { PatientMenu } from '@/uiKit/molecules/PatientNavigation/PatientMenu.tsx';

export const PATIENT_NAVIGATION_HEIGHT = '6rem';
export const PATIENT_NAVIGATION_TOP_PADDING = '2vh';

export type PatientNavigationHeaderProps = {
  className?: string;
};

export const PatientNavigationHeader: React.FC<
  PatientNavigationHeaderProps
> = ({ className }) => {
  const styles = useStyles(makeStyles);
  const patient = usePatientFromURL();

  return (
    <div
      id="manage-patient-navigation"
      className={cx(styles.container, className)}
    >
      <div className={styles.top}>
        <PatientHeaderInfo patient={patient} />
        <PatientActions />
      </div>
      <PatientMenu />
    </div>
  );
};

export const PatientNavigationHeaderSkeleton = () => {
  const styles = useStyles(makeStyles);

  return (
    <div className={styles.skeleton}>
      <div className={css({ display: 'flex' })}>
        <Skeleton
          variant="text"
          width="20%"
          className={css({ fontSize: FontSizes.subtitleLarge })}
        />
      </div>
    </div>
  );
};

const makeStyles = (theme: Theme) => ({
  container: css`
    height: ${PATIENT_NAVIGATION_HEIGHT};
    position: sticky;
    top: 0;
    width: 100%;
    display: flex;
    flex-direction: column;
    background-color: ${theme.palette.common.white};
    padding: 2vh 2vw 0 2vw;
    gap: ${theme.spacing(2)};
    border-bottom: ${theme.spacing(1)} solid ${theme.palette.background.default};
    z-index: 10;
  `,
  skeleton: css({
    height: PATIENT_NAVIGATION_HEIGHT, // Name + NavBar + Gap
    width: '100%',
    backgroundColor: theme.palette.common.white,
    padding: `${PATIENT_NAVIGATION_TOP_PADDING} 2vw 0 2vw`,
  }),
  top: css`
    display: flex;
    flex: 1 1;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: ${theme.spacing(8)};
  `,
});
